import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import Home from './components/Home';
import { PrivateRoute, PublicRoute } from './routes/ProtectedRoutes';
import SeeContent from './components/SeeContent';
import Camera from './components/Camera';
import ProfileUpload from './components/ProfileUpload';
import Profile from './components/Profile';
import YourTrippins from './components/YourTrippins';
import TrippinPlaceSelect from './components/TrippinPlaceSelect';
import TrippinPhotoSelect from './components/TrippinPhotoSelect';
import TrippinReviewPost from './components/TrippinReviewPost';
import { connectSocket } from './utils/WebSocket';
import Notifications from './components/Notifications';
import PlaceView from "./components/PlaceView";
import Saved from "./components/Saved";
import StalkersList from './components/StalkersList';
import StalkingList from './components/StalkingList';
import EditCloseFriends from './components/EditCloseFirends';
import { App as CapacitorApp } from '@capacitor/app';
import MyTrippinGrid from './components/MyTrippinGrid';
import TrippinsTabs from './components/TrippinTabs';
import MyTrippinTabs from './components/MyTrippinTabs';
import { PushNotifications } from '@capacitor/push-notifications';
import { isPlatform } from '@ionic/react';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import { SocketProvider } from "./context/SocketContext";
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext'; // adjust path if needed
import VerifyEmail from './components/Auth/VerifyEmail';
import RequireInit from './RequireInit';
import ManageSessions from './components/ManageSessions';
import AppearanceSettings from './components/AppearanceSettings';
import "react-datepicker/dist/react-datepicker.css";
import { getUsername, getProfilePicture } from "./utils/userUtils";
import { useSocket } from "./context/SocketContext";

// Business Admin Panel Components
import BusinessAdminPanel from './components/BusinessSuite/BusinessAdminPanel';
import StripeOnboardingReturn from './components/business-onboarding/StripeOnboardingReturn';
import StripeOnboardingRefresh from './components/business-onboarding/StripeOnboardingRefresh';
import LocationLetterReturn from './components/business-onboarding/LocationLetterReturn';
import LocationLetterVerification from './components/business-onboarding/LocationLetterVerification';
import Inbox from './components/BusinessSuite/Inbox';
import ManageFlashes from './components/BusinessSuite/ManageFlashes';
import PinTrippins from './components/BusinessSuite/PinTrippins';
import BusinessPostForm from './components/BusinessSuite/BusinessPostForm';
import MetricsBoard from './components/BusinessSuite/analytics/MetricsBoard';
import DiscountsPage from './components/BusinessSuite/discounts/pages/DiscountsPage';
import EmployeeConfigPanel from './components/BusinessSuite/employeesConfiguration/EmployeeConfigPanel';
import ScheduleRoot from './components/BusinessSuite/schedule/index';
import CalendarView from './components/BusinessSuite/schedule/Calendar/CalendarView';
import TimeOffView from './components/BusinessSuite/schedule/TimeOff/TimeOffView';
import ProductManagerPanel from './components/BusinessSuite/store/products_config/components/ProductManagerPanel';
import StoreSettingsWizard from './components/BusinessSuite/store/store_settings/index';

// Business Onboarding Components
import BusinessEntry from './components/business-onboarding/BusinessEntry';
import BusinessOnboardingWizard from './components/business-onboarding/BusinessOnboardingWizard';

// Reservation Components
import BookReservationPage from './components/Reservations/pages/BookReservationPage';
import ManageReservationsPage from './components/Reservations/pages/ManageReservationsPage';
import PaymentPage from './components/Reservations/pages/PaymentPage';
import PaymentSuccessPage from './components/Reservations/pages/PaymentSuccessPage';
import ReservationConfigForm from './components/ReservationsConfig/ReservationConfigForm';
import { TripCreatorForm } from './features/trip-creator';


function AppShell({ children }) {
  const { incomingCall, setIncomingCall, socket } = useSocket();
  const navigate = useNavigate();

  const [callerName, setCallerName] = useState("");
  const [callerImage, setCallerImage] = useState(null);
  const [ringtoneAudio, setRingtoneAudio] = useState(null);

  useEffect(() => {
    if (incomingCall?.from) {
      getUsername(incomingCall.from).then(name => setCallerName(name));
      getProfilePicture(incomingCall.from).then(url => setCallerImage(url));

      const audio = new Audio("/assets/sounds/ringtone.mp3");
      audio.loop = true;
      audio.play().catch(err =>
        console.error("Autoplay blocked by browser:", err)
      );
      setRingtoneAudio(audio);

      if (navigator.vibrate) {
        navigator.vibrate([500, 300, 500, 300, 500, 300]);
      }
    }
    else {
      if (ringtoneAudio) {
        ringtoneAudio.pause();
        ringtoneAudio.currentTime = 0;
        setRingtoneAudio(null);
      }
      if (navigator.vibrate) {
        navigator.vibrate(0);
      }
    }
  }, [incomingCall]);

  useEffect(() => {
    if (!socket) return;

    const clearIncoming = (payload) => {
      if (incomingCall) {
        setIncomingCall(null);
      }
    };

    socket.on('call-canceled', clearIncoming);
    socket.on('call-rejected', clearIncoming);

    return () => {
      socket.off('call-canceled', clearIncoming);
      socket.off('call-rejected', clearIncoming);
    };
  }, [socket, incomingCall, setIncomingCall]);

  const accept = () => {
    if (!incomingCall) return;
    socket.emit("accept-call", { to: incomingCall.from });

    navigate(`/call/${incomingCall.from}`, {
      state: { peer: { id: incomingCall.from, name: callerName } },
    });

    setIncomingCall(null);
  };

  const decline = () => {
    if (!incomingCall) return;
    socket.emit("cancel-call", { to: incomingCall.from });
    socket.emit("reject-call", { to: incomingCall.from });
    setIncomingCall(null);
  };

  return (
    <>
      {incomingCall ? (
        <IncomingCall
          callerName={callerName || "Unknown"}
          callerImage={callerImage}
          onAccept={accept}
          onDecline={decline}
        />
      ) : (
        children
      )}
    </>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  // Avoid destructuring before confirming auth is defined
  const accessToken = auth?.accessToken;
  const userId = auth?.userId;
  const initialized = auth?.initialized;

// Stripe publishable key for client-side init
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.STRIPE_PUBLISHABLE_KEY;
  useEffect(() => {
    try {
      if (userId) {
        connectSocket(userId);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }, [userId]);


  useEffect(() => {
    // ONLY run on mobile platforms
    if (isPlatform('android') && accessToken) {
      try {
        // Function to register for push notifications and save the token
        const registerPushNotifications = async () => {
          try {
            // Check the permission status
            const permissionStatus = await PushNotifications.checkPermissions();
            if (permissionStatus.receive !== 'granted') {
              permissionStatus = await PushNotifications.requestPermissions();
              console.log("Requested permission status:", permissionStatus);
            }
            if (permissionStatus.receive === 'granted') {
              console.log('Permission granted. Registering for push notifications...');
              // Handle successful registration
              PushNotifications.addListener('registration', async (token) => {
                console.log('Push registration success:', token.value);
                // Save the FCM token to the backend
                await fetch('https://api.trippin.icu/api/device-tokens', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify({ deviceToken: token.value }),
                })
                  .then(response => response.json())
                  .then(data => console.log("Token stored successfully:", data))
                  .catch(error => console.error("Error saving token:", error));
              });

              // Handle registration error
              PushNotifications.addListener('registrationError', (error) => {
                console.error('Push registration error:', error);
              });
              await PushNotifications.register();
            }
            else {
              console.warn('Push notifications permission not granted.');
            }
          } catch (error) {
            console.error('Error during push notification registration:', error);
          }
        };

        // Register for push notifications when the app is in the foreground
        CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
          if (isActive) {
            console.log('App is active. Checking push notification permissions...');
            await registerPushNotifications();
          }

        });

        registerPushNotifications();
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, [accessToken]);

  useEffect(() => {
    if (isPlatform('ios') && accessToken) {
      console.log("🔄 Waiting for FCM token from iOS...");

      // Listen for event from Swift (token will be sent from AppDelegate)
      window.addEventListener("FCMTokenReceived", async (event) => {
        const token = event.detail.token;
        console.log("✅ Received FCM Token from Native:", token);

        try {
          await fetch('https://api.trippin.icu/api/device-tokens', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ deviceToken: token }),
          });
          console.log("✅ Token stored successfully in backend");
        } catch (error) {
          console.error("❌ Error saving token:", error);
        }
      });

      console.log("📡 Waiting for FCM token event...");
    }
  }, [accessToken]);

  useEffect(() => {
    let backButtonListener;

    const handleBackButton = () => {
      if (location.pathname === '/home' || location.pathname === '/login') {
        // Exit app if on the home or login page
        CapacitorApp.exitApp();
      } else {
        // Navigate back
        navigate(-1);
      }
    };

    // Add listener for hardware back button
    const addBackButtonListener = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', handleBackButton);
    };

    addBackButtonListener();

    // Cleanup listener on component unmount
    return () => {
      if (backButtonListener && typeof backButtonListener.remove === 'function') {
        backButtonListener.remove(); // Properly remove the listener
      }
    };
  }, [location, navigate]);

  if ('serviceWorker' in navigator) {
    if (!window.Capacitor || !window.Capacitor.isNativePlatform()) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    } else {
      console.log('Skipping Service Worker for Capacitor WebView');
    }
  }

  return (
    <SocketProvider>
      <RequireInit>
        <AnimatePresence mode="wait">
          <AppShell>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/verify-email" element={<PublicRoute><VerifyEmail /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
              <Route path="/privacypolicy" element={<PublicRoute><PrivacyPolicy /></PublicRoute>} />
              <Route path="/termsandconditions" element={<PublicRoute><TermsAndConditions /></PublicRoute>} />

              {/* Private Routes */}
              <Route path="*" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
              <Route path="/trip-creator" element={<PrivateRoute><TripCreatorForm /></PrivateRoute>} />
              <Route path="/profile-upload" element={<PrivateRoute><ProfileUpload /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/profile/:userIdPar" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/yourtrippins/:trippinId/placesSelect" element={<PrivateRoute><TrippinPlaceSelect isTagged={false} /></PrivateRoute>} />
              <Route path="/taggedtrippins/:trippinId/placesSelect" element={<PrivateRoute><TrippinPlaceSelect isTagged={true} /></PrivateRoute>} />
              <Route path="/taggedtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={true} /></PrivateRoute>} />          <Route path="/yourtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={false} /></PrivateRoute>} />
              <Route path="/taggedtrippins/:trippinId/reviewPost" element={<PrivateRoute><TrippinReviewPost isTagged={true} /></PrivateRoute>} />
              <Route path="/yourtrippins/:trippinId/reviewPost" element={<PrivateRoute><TrippinReviewPost isTagged={false} /></PrivateRoute>} />
              <Route path="/yourtrippins" element={<PrivateRoute><YourTrippins /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
              <Route path="/trippinpostgrid" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/saved" element={<PrivateRoute><Saved /></PrivateRoute>} />
              <Route path="/places/:placeId" element={<PrivateRoute><PlaceView /></PrivateRoute>} />
              <Route path="/profile/:userId/stalkers" element={<PrivateRoute><StalkersList /></PrivateRoute>} />
              <Route path="/profile/:userId/stalking" element={<PrivateRoute><StalkingList /></PrivateRoute>} />
              <Route path="/edit-close-friends" element={<PrivateRoute><EditCloseFriends /></PrivateRoute>} />
              <Route path="/mytrippins" element={<PrivateRoute><MyTrippinGrid /></PrivateRoute>} />
              <Route path="/tabs" element={<PrivateRoute><TrippinsTabs /></PrivateRoute>} />
              <Route path="/mytabs" element={<PrivateRoute><MyTrippinTabs /></PrivateRoute>} />
              <Route path="/yourtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={false} /></PrivateRoute>} />
              <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
              <Route path="/manage-sessions" element={<PrivateRoute><ManageSessions /></PrivateRoute>} />
              <Route path="/settings/appearance" element={<PrivateRoute><AppearanceSettings /></PrivateRoute>} />
              <Route path="/camera" element={<PrivateRoute><Camera /></PrivateRoute>} />
              <Route path="/see-content" element={<PrivateRoute><SeeContent /></PrivateRoute>} />

              {/* Reservation Routes */}
              <Route path="/reservations/book/:businessId" element={<PrivateRoute><BookReservationPage /></PrivateRoute>} />
              <Route path="/reservations/manage" element={<PrivateRoute><ManageReservationsPage /></PrivateRoute>} />
              <Route path="/reservations/payment" element={<PrivateRoute><PaymentPage /></PrivateRoute>} />
              <Route path="/reservations/success" element={<PrivateRoute><PaymentSuccessPage /></PrivateRoute>} />
              <Route path="/business-admin/reservations/config" element={<PrivateRoute><ReservationConfigForm /></PrivateRoute>} />

              {/* Business Onboarding Routes */}
              <Route path="/business" element={<PrivateRoute><BusinessEntry /></PrivateRoute>} />
              <Route path="/business/onboarding/new" element={<PrivateRoute><BusinessOnboardingWizard /></PrivateRoute>} />
              <Route path="/business/onboarding/:businessId" element={<PrivateRoute><BusinessOnboardingWizard /></PrivateRoute>} />
              <Route path="/business/:businessId/onboarding/stripe-return" element={<PrivateRoute><StripeOnboardingReturn /></PrivateRoute>} />
              <Route path="/business/:businessId/onboarding/stripe-refresh" element={<PrivateRoute><StripeOnboardingRefresh /></PrivateRoute>} />
              <Route path="/business/:businessId/onboarding/location-letter-return" element={<PrivateRoute><LocationLetterReturn /></PrivateRoute>} />
              <Route path="/business/:businessId/onboarding/location-letter-verify" element={<PrivateRoute><LocationLetterVerification /></PrivateRoute>} />
              {/* Stripe can be configured (or historically has been configured) with plural business routes */}
              <Route path="/businesses/:businessId/onboarding/stripe-return" element={<PrivateRoute><StripeOnboardingReturn /></PrivateRoute>} />
              <Route path="/businesses/:businessId/onboarding/stripe-refresh" element={<PrivateRoute><StripeOnboardingRefresh /></PrivateRoute>} />

              {/* Business Admin Panel Routes */}
              <Route path="/business-admin" element={<PrivateRoute><BusinessAdminPanel /></PrivateRoute>} />
              <Route path="/business-admin/inbox" element={<PrivateRoute><Inbox /></PrivateRoute>} />
              <Route path="/business-admin/flashes" element={<PrivateRoute><ManageFlashes /></PrivateRoute>} />
              <Route path="/business-admin/pin-trippins" element={<PrivateRoute><PinTrippins /></PrivateRoute>} />
              <Route path="/business-admin/create-post" element={<PrivateRoute><BusinessPostForm /></PrivateRoute>} />
              <Route path="/business-admin/analytics" element={<PrivateRoute><MetricsBoard /></PrivateRoute>} />
              <Route path="/business-admin/discounts" element={<PrivateRoute><DiscountsPage /></PrivateRoute>} />
              <Route path="/business-admin/employees" element={<PrivateRoute><EmployeeConfigPanel /></PrivateRoute>} />
              <Route path="/business-admin/schedule" element={<PrivateRoute><ScheduleRoot /></PrivateRoute>} />
              <Route path="/business-admin/schedule/calendar" element={<PrivateRoute><CalendarView /></PrivateRoute>} />
              <Route path="/business-admin/schedule/timeoff" element={<PrivateRoute><TimeOffView /></PrivateRoute>} />
              <Route path="/business-admin/store/products" element={<PrivateRoute><ProductManagerPanel /></PrivateRoute>} />
              <Route path="/business-admin/store/settings" element={<PrivateRoute><StoreSettingsWizard /></PrivateRoute>} />
            </Routes>
          </AppShell>
        </AnimatePresence>
      </RequireInit>
    </SocketProvider>
  );
}

export default App;
